import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    IconCheck,
    IconRotate,
    IconRotateClockwise,
    IconX,
    IconZoomIn,
    IconZoomOut,
} from '@tabler/icons-react';
import { useTranslation } from '../../../../../../i18n/hooks/useTranslation';
import {
    ATS_PHOTO_ASPECTS,
    DEFAULT_ATS_PHOTO_CROP,
    clampAtsPhotoCrop,
    defaultAtsPhotoCrop,
    drawAtsPhotoCrop,
    renderAtsPhotoCrop,
} from '../engines/atsCvPhotoCrop';

/**
 * Choose what the CV's photo box shows.
 *
 * A CV photo is a fixed box on a printed page, so SOMETHING has to be cut off
 * a picture that is not that shape. The old field decided for the user — a
 * square out of the middle — which on an ordinary portrait snapshot takes the
 * top of the head off and leaves the chest in. This gives the decision back:
 * drag the picture, zoom it, turn it, and pick the shape of the box itself.
 *
 * The frame here and the box on the page are the same rectangle, drawn by the
 * same function, so what is inside these lines is what prints.
 */
export default function AtsCvPhotoCropModal({ source, crop: initialCrop, aspect: initialAspect, onApply, onClose }) {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const dragRef = useRef(null);
    const [image, setImage] = useState(null);
    const [crop, setCrop] = useState(() => ({
        ...DEFAULT_ATS_PHOTO_CROP,
        ...(initialAspect ? { aspect: initialAspect } : null),
        ...(initialCrop || {}),
    }));
    const [frame, setFrame] = useState({ width: 0, height: 0 });

    const aspect = crop.aspect || 1;

    // Whether this picture has been framed before. A fresh one gets the
    // top-biased default once its size and the frame's are both known.
    const placedRef = useRef(Boolean(initialCrop));

    useEffect(() => {
        let alive = true;
        const el = new Image();
        el.onload = () => { if (alive) setImage(el); };
        el.src = source;
        return () => { alive = false; };
    }, [source]);

    useEffect(() => {
        if (!image || !frame.width || placedRef.current) return;
        placedRef.current = true;
        setCrop((prev) => defaultAtsPhotoCrop(image, frame.width, frame.height, prev.aspect));
    }, [image, frame]);

    // The frame is as big as the dialog allows at the chosen shape, so the
    // picture is judged at a size worth judging it at.
    useEffect(() => {
        const measure = () => {
            const maxW = Math.min(460, window.innerWidth - 80);
            // The dialog's chrome — header, hint, shape row, zoom row, turn
            // row, footer — comes off first: a frame that fills the window
            // leaves the controls behind the footer, where they cannot be used.
            const maxH = Math.max(160, window.innerHeight - 400);
            const width = Math.max(160, Math.min(maxW, maxH * aspect));
            setFrame({ width: Math.round(width), height: Math.round(width / aspect) });
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [aspect]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose]);

    /* ------------------------------------------------------------- drawing */

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !image || !frame.width) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = Math.round(frame.width * dpr);
        canvas.height = Math.round(frame.height * dpr);
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawAtsPhotoCrop(ctx, image, frame.width, frame.height, crop);
    }, [image, frame, crop]);

    /* --------------------------------------------------------- interaction */

    const setSafe = useCallback((next) => {
        setCrop((prev) => clampAtsPhotoCrop({ ...prev, ...next }, image, frame.width, frame.height));
    }, [image, frame.width, frame.height]);

    const onPointerDown = (e) => {
        if (!image) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = { x: e.clientX, y: e.clientY, offsetX: crop.offsetX, offsetY: crop.offsetY };
    };

    const onPointerMove = (e) => {
        const drag = dragRef.current;
        if (!drag || !frame.width) return;
        setSafe({
            offsetX: drag.offsetX + (e.clientX - drag.x) / frame.width,
            offsetY: drag.offsetY + (e.clientY - drag.y) / frame.height,
        });
    };

    const endDrag = () => { dragRef.current = null; };

    const onWheel = (e) => {
        if (!image) return;
        e.preventDefault();
        setSafe({ zoom: crop.zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08) });
    };

    // A turn keeps the picture centred rather than trying to preserve a pan
    // that no longer means anything once the axes have swapped.
    const turn = (by) => setCrop((prev) => clampAtsPhotoCrop(
        { ...prev, rotation: (((prev.rotation + by) % 360) + 360) % 360, offsetX: 0, offsetY: 0 },
        image,
        frame.width,
        frame.height,
    ));

    const apply = () => {
        if (!image) return;
        onApply({ dataUrl: renderAtsPhotoCrop(image, crop), crop });
    };

    const zoomPercent = useMemo(() => Math.round(crop.zoom * 100), [crop.zoom]);

    if (typeof document === 'undefined') return null;

    // Through a portal, always. `position: fixed` is measured against the
    // nearest ancestor carrying a transform, a filter or a backdrop-filter
    // rather than against the window — and the site wraps this field in a
    // locked block that greys it with `filter: grayscale(1)`. Rendered in
    // place, this "full screen" dialog came out 78px tall, inside that block,
    // and grey.
    return createPortal((
        <div
            className="fixed inset-0 z-[300] flex flex-col bg-[#1C1815]/80 backdrop-blur-[2px]"
            role="presentation"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('atsCvMaker.photo.cropTitle')}
                onClick={(e) => e.stopPropagation()}
                className="m-auto flex max-h-[96vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_24px_60px_rgba(28,24,21,0.35)] dark:border-zinc-700 dark:bg-zinc-900"
            >
                <div className="flex h-12 flex-none items-center justify-between border-b border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <h2 className="text-[14px] font-bold text-[#1C1815] dark:text-zinc-100">
                        {t('atsCvMaker.photo.cropTitle')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-[#3D3833] dark:hover:bg-zinc-800"
                    >
                        <IconX size={16} stroke={2} />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="flex flex-col items-center gap-3 bg-[#F4F1ED] px-4 py-5 dark:bg-zinc-950">
                        <div
                            className="relative touch-none overflow-hidden rounded-lg bg-white shadow-[0_2px_14px_rgba(28,24,21,0.16)]"
                            style={{ width: frame.width, height: frame.height, cursor: image ? 'grab' : 'default' }}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={endDrag}
                            onPointerCancel={endDrag}
                            onWheel={onWheel}
                        >
                            <canvas
                                ref={canvasRef}
                                className="block h-full w-full"
                                style={{ width: frame.width, height: frame.height }}
                            />
                            {/* Thirds, drawn over the picture: the one piece of
                                chrome that actually helps place a face. */}
                            <div className="pointer-events-none absolute inset-0">
                                <div className="absolute inset-y-0 left-1/3 w-px bg-white/45" />
                                <div className="absolute inset-y-0 left-2/3 w-px bg-white/45" />
                                <div className="absolute inset-x-0 top-1/3 h-px bg-white/45" />
                                <div className="absolute inset-x-0 top-2/3 h-px bg-white/45" />
                                <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-[#1C1815]/15" />
                            </div>
                        </div>

                        <p className="text-center text-[12px] text-[#6B635C] dark:text-zinc-400">
                            {t('atsCvMaker.photo.cropHint')}
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="mr-1 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                {t('atsCvMaker.photo.shape')}
                            </span>
                            {ATS_PHOTO_ASPECTS.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => setSafe({ aspect: option.value })}
                                    aria-pressed={Math.abs(aspect - option.value) < 0.001}
                                    className={`inline-flex h-8 items-center rounded-lg border px-2.5 text-[12px] font-semibold transition-colors ${
                                        Math.abs(aspect - option.value) < 0.001
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                                    }`}
                                >
                                    {t(`atsCvMaker.photo.aspects.${option.id}`)}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setSafe({ zoom: crop.zoom / 1.15 })}
                                aria-label={t('atsCvMaker.photo.zoomOut')}
                                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-[#ECE8E4] bg-white text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconZoomOut size={15} stroke={1.8} />
                            </button>
                            <input
                                type="range"
                                min={50}
                                max={400}
                                value={zoomPercent}
                                onChange={(e) => setSafe({ zoom: Number(e.target.value) / 100 })}
                                aria-label={t('atsCvMaker.photo.zoom')}
                                className="h-1.5 flex-1 cursor-pointer accent-[color:var(--color-primary,#E0492B)]"
                            />
                            <button
                                type="button"
                                onClick={() => setSafe({ zoom: crop.zoom * 1.15 })}
                                aria-label={t('atsCvMaker.photo.zoomIn')}
                                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-[#ECE8E4] bg-white text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconZoomIn size={15} stroke={1.8} />
                            </button>
                            <span className="w-12 flex-none text-right text-[12px] tabular-nums text-[#6B635C] dark:text-zinc-400">
                                {zoomPercent}%
                            </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => turn(-90)}
                                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconRotate size={14} stroke={1.8} />
                                {t('atsCvMaker.photo.rotateLeft')}
                            </button>
                            <button
                                type="button"
                                onClick={() => turn(90)}
                                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconRotateClockwise size={14} stroke={1.8} />
                                {t('atsCvMaker.photo.rotateRight')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setCrop({ ...DEFAULT_ATS_PHOTO_CROP, aspect })}
                                className="inline-flex h-8 items-center rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                            >
                                {t('atsCvMaker.photo.reset')}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-800">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-9 items-center rounded-lg border border-[#ECE8E4] bg-white px-3.5 text-[12.5px] font-semibold text-[#3D3833] transition-colors hover:border-[#D9D4CE] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={apply}
                        disabled={!image}
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                    >
                        <IconCheck size={15} stroke={2} />
                        {t('atsCvMaker.photo.applyCrop')}
                    </button>
                </div>
            </div>
        </div>
    ), document.body);
}
